/**
 * StepHostContext - Host (property address) + early holding attributes.
 *
 * Collects the property address AND key holding attributes (e.g. installation
 * date, installer name) BEFORE product selection. This enables the geo
 * eligibility API to filter products by location + date.
 *
 * Two-column layout: left (host/property), right (early attributes).
 * On "Next", fires geo eligibility lookup and stores results in wizard state.
 */

import { useState, useMemo, useCallback, Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Home, MapPin, Pencil } from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { useAddressAutocomplete, useAddressResolve } from '@/admin/hooks/use-lookups'
import type { AddressSuggestion, ResolvedAddress } from '@/admin/hooks/use-lookups'
import type { WizardState, GeoEligibilityResult } from '@/admin/pages/registrations/new-registration-page'
import type { HostTemplate, HostAttributeMeta } from '@/admin/types/hosts'



import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import api from '@/admin/lib/api'
import { RegistrationPanel, RegistrationStepActions, RegistrationStepGrid } from './layout'
import { InlineDealerField } from './shared'
import { MailingAddressPanel } from './mailing-address-panel'
import {
  useDataCaptureConfig,
  isVisible,
  isRequired,
} from '@/admin/hooks/use-data-capture-config'
import type { DataCaptureConfig } from '@/admin/hooks/use-registration-settings'
import { MetadataField, normaliseTemplateAttribute } from '@/shared/lib/forms'
import {
  isValueFilled,
  useRequiredFieldErrors,
  type RequiredFieldSpec,
} from './wizard-validation'
import { useExternalRoles } from '@/admin/hooks/use-external-roles'
import { usePartnerPickerScope } from '@/admin/hooks/use-partner-picker-scope'
import { usePartnershipDetail } from '@/admin/hooks/use-partnerships'
import { PartnerProfilePanel } from '@/admin/components/shared/partner-profile-panel'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { AddressCapture } from './address-capture'
import {
  StructurePickerPanel,
  type StructurePickerActivation,
} from './structure-picker-panel'
import {
  normaliseAddressObject as normaliseCapturedAddressObject,
  formatAddress as formatCapturedAddress,
} from './address-utils'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import { evaluatePredicateLocally } from '@/admin/lib/rules/local-validation'

interface RegistrationRuleContext {
  fields?: Record<string, unknown>
  entities?: Record<string, Record<string, unknown>>
  aggregates?: Record<string, unknown>
  roles?: string[]
  permissions?: string[]
}

/** Fetch full host template detail (with attributes) by UUID. */
function useHostTemplateDetail(uuid: string | null) {
  return useQuery<HostTemplate>({
    queryKey: ['host-template-detail', uuid],
    queryFn: () => api.get(`/hosts/templates/${uuid}/`).then((r) => r.data),
    enabled: !!uuid,
  })
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TemplateAttribute {
  uuid: string
  code_name: string
  display_name: string
  field_type: string
  configuration_data?: { options?: { value: string; label: string }[] }
}

interface StepHostContextProps {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
  back?: () => void
  continueDisabled?: boolean
  continueHint?: string | null
  hostTemplate: HostTemplate | null
  hostRequirement: string
  earlyAttributes: TemplateAttribute[]
  geoEnabled: boolean
  /**
   * Extra panel(s) to render alongside the step's own panels in the
   * shared `RegistrationStepGrid`. Used by the page to slot in the
   * shared `RegistrationFieldsPanel`.
   */
  extraPanels?: React.ReactNode
  /**
   * Tenant-defined RegistrationField inputs that should render *inside*
   * the Property panel rather than in their own panel. The page passes
   * the inline-variant `RegistrationFieldsPanel` here for any registration
   * fields whose visibility rule does not reference a host attribute -
   * fields that *do* reference a host attribute are slotted directly
   * under their trigger via `anchoredFieldsByHostAttr` instead.
   */
  propertyExtras?: React.ReactNode
  /**
   * Registration-field nodes anchored to a specific host attribute via
   * a field-visibility rule (e.g. `other_structure_type` shown when
   * `structure_type === "Other"`), keyed by the host attribute's
   * `code_name`. Each entry renders inline immediately below the
   * matching host attribute input so the dependency reads naturally.
   */
  anchoredFieldsByHostAttr?: Record<string, React.ReactNode>
  dataCaptureConfig?: DataCaptureConfig
  earlyCaptureFields?: string[]
  ruleContext?: RegistrationRuleContext
  /**
   * Bubbles the structure picker's activation up to the page so it can
   * drop picker-owned registration fields (e.g. `other_structure_type`)
   * from this step's validation - those values flow via the picker into
   * `hostAttributeValues`, not `registrationFieldValues`.
   */
  onStructurePickerActivation?: (activation: StructurePickerActivation) => void
}

// ---------------------------------------------------------------------------
// Address lookup panel
// ---------------------------------------------------------------------------

/** Bridge host ``maps_to_column`` (EAV / Host columns) to resolve API keys. */
const ADDRESS_MAPS_TO_RESOLVED: Record<string, keyof ResolvedAddress> = {
  line_1: 'line_1',
  address_line_1: 'line_1',
  line_2: 'line_2',
  address_line_2: 'line_2',
  town_or_city: 'town_or_city',
  city: 'town_or_city',
  town: 'town_or_city',
  address_town_or_city: 'town_or_city',
  county: 'county',
  address_county: 'county',
  postcode: 'postcode',
  post_code: 'postcode',
  address_postcode: 'postcode',
  country: 'country',
  address_country: 'country',
}

const ADDRESS_FIELD_TO_RESOLVED: Record<string, keyof ResolvedAddress> = {
  addressLine1: 'line_1',
  addressLine2: 'line_2',
  locality: 'town_or_city',
  region: 'county',
  postalCode: 'postcode',
  countryCode: 'country',
}

function resolvedToAddressFieldValue(resolved: ResolvedAddress): Record<string, string> {
  return {
    addressLine1: resolved.line_1 ?? '',
    addressLine2: resolved.line_2 ?? '',
    locality: resolved.town_or_city ?? '',
    region: resolved.county ?? '',
    postalCode: resolved.postcode ?? '',
    countryCode: resolved.country ?? '',
  }
}

function addressFieldValueToResolved(
  value: unknown,
): Partial<Record<keyof ResolvedAddress, string>> {
  if (!value || typeof value !== 'object') return {}
  const record = value as Record<string, unknown>
  const out: Partial<Record<keyof ResolvedAddress, string>> = {}
  for (const [fieldKey, resolvedKey] of Object.entries(ADDRESS_FIELD_TO_RESOLVED)) {
    const raw = record[fieldKey]
    if (raw !== undefined && raw !== null && String(raw).trim().length > 0) {
      out[resolvedKey] = String(raw)
    }
  }
  return out
}

function addressFieldValueToAttributeValues(
  value: unknown,
  attributes: HostAttributeMeta[],
): Record<string, string> {
  const resolvedParts = addressFieldValueToResolved(value)
  const out: Record<string, string> = {}
  for (const attr of attributes) {
    if (!attr.maps_to_column) continue
    const resolvedKey = ADDRESS_MAPS_TO_RESOLVED[attr.maps_to_column]
    if (!resolvedKey) continue
    const part = resolvedParts[resolvedKey]
    if (part) out[attr.code_name] = part
  }
  return out
}

function addressPostalCode(value: unknown): string {
  if (!value || typeof value !== 'object') return ''
  const record = value as Record<string, unknown>
  const nested =
    record.value && typeof record.value === 'object'
      ? (record.value as Record<string, unknown>)
      : null
  return String(
    record.postalCode ??
      record.postcode ??
      record.postal_code ??
      record.address_postcode ??
      nested?.postalCode ??
      nested?.postcode ??
      nested?.postal_code ??
      nested?.address_postcode ??
      '',
  )
}

function AddressPanel({
  attributes,
  hostValues,
  onValuesChange,
  addressPlaceholder,
  anchoredExtras,
  error,
  attributeErrors,
}: {
  attributes: HostAttributeMeta[]
  hostValues: Record<string, unknown>
  onValuesChange: (values: Record<string, unknown>) => void
  addressPlaceholder: string
  /**
   * Registration-field nodes (or any ReactNode) keyed by host attribute
   * `code_name`. Each entry renders inline immediately after its
   * matching host attribute input so visibility-rule dependents (e.g.
   * `other_structure_type` driven by `structure_type`) sit next to
   * their trigger instead of at the bottom of the panel.
   */
  anchoredExtras?: Record<string, React.ReactNode>
  /**
   * Required-field error from the wizard validation context. When set,
   * the search input shows a red border and the message renders below
   * - matching the inline-error treatment used by `MetadataField`.
   */
  error?: string
  /**
   * Per-attribute error map keyed by host-attribute `code_name`. Used
   * to surface required-field errors on standard (non-address-lookup,
   * non-prepopulated) host attributes such as `structure_type`.
   */
  attributeErrors?: Record<string, string>
}) {
  const compositeAddressAttr = attributes.find((a) => a.field_type === 'ADDRESS')
  const lookupAttr =
    attributes.find(
      (a) => a.lookup_provider === 'address' && a.field_type !== 'ADDRESS',
    ) ??
    (compositeAddressAttr?.lookup_provider === 'address'
      ? compositeAddressAttr
      : undefined)
  const prepopAttrs = attributes.filter(
    (a) => a.is_prepopulated && a.field_type !== 'ADDRESS',
  )
  const standardAttrs = attributes.filter(
    (a) =>
      a.field_type !== 'ADDRESS' &&
      !a.is_prepopulated &&
      a.lookup_provider !== 'address',
  )
  const addressFieldAttrs = useMemo(
    () =>
      compositeAddressAttr
        ? [compositeAddressAttr]
        : [...(lookupAttr ? [lookupAttr] : []), ...prepopAttrs],
    [compositeAddressAttr, lookupAttr, prepopAttrs],
  )
  const [query, setQuery] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  // Snapshot of hostValues taken when the modal opens so a Cancel/dismiss
  // restores the prior state. Edits inside the modal write directly to
  // parent state (live preview) per the wizard's existing pattern.
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null)
  const { data: suggestions } = useAddressAutocomplete(query)
  const resolve = useAddressResolve()

  const handleSelect = (suggestion: AddressSuggestion) => {
    resolve.mutate(
      { id: suggestion.id, term: query },
      {
        onSuccess: (resolved: ResolvedAddress) => {
          const next = { ...hostValues }
          if (compositeAddressAttr) {
            next[compositeAddressAttr.code_name] = resolvedToAddressFieldValue(resolved)
          }
          for (const attr of attributes) {
            if (!attr.maps_to_column) continue
            const resolvedKey = ADDRESS_MAPS_TO_RESOLVED[attr.maps_to_column]
            if (!resolvedKey) continue
            const val = resolved[resolvedKey]
            if (val !== undefined && val !== null && String(val).length > 0) {
              next[attr.code_name] = val
            }
          }
          onValuesChange(next)
          setQuery('')
        },
      },
    )
  }

  const hasAddress =
    (compositeAddressAttr
      ? Object.values(
          addressFieldValueToResolved(hostValues[compositeAddressAttr.code_name]),
        ).some((value) => !!String(value).trim())
      : false) ||
    prepopAttrs.some((a) => !!String(hostValues[a.code_name] ?? '').trim()) ||
    !!(
      lookupAttr &&
      lookupAttr.field_type !== 'ADDRESS' &&
      String(hostValues[lookupAttr.code_name] ?? '').trim()
    )

  // Build a formatted summary of the saved address grouped by canonical
  // resolved key so we can render it as a multi-line block under the
  // search input regardless of whether it came from lookup or manual.
  const addressByKey = useMemo(() => {
    const out: Partial<Record<keyof ResolvedAddress, string>> = compositeAddressAttr
      ? addressFieldValueToResolved(hostValues[compositeAddressAttr.code_name])
      : {}
    for (const attr of attributes) {
      if (!attr.maps_to_column) continue
      const k = ADDRESS_MAPS_TO_RESOLVED[attr.maps_to_column]
      if (!k) continue
      const v = String(hostValues[attr.code_name] ?? '').trim()
      if (v) out[k] = v
    }
    return out
  }, [attributes, compositeAddressAttr, hostValues])

  const handleAddressValueChange = (attr: HostAttributeMeta, value: unknown) => {
    const next = { ...hostValues, [attr.code_name]: value }
    if (attr.field_type === 'ADDRESS') {
      Object.assign(next, addressFieldValueToAttributeValues(value, attributes))
    }
    onValuesChange(next)
  }

  const formattedAddress = formatCapturedAddress(addressByKey)
  // Memoised so the reference stays stable across re-renders (e.g. the
  // background geo-eligibility lookup firing on postcode entry). Without
  // this, a new object every render makes AddressCapture's internal form
  // re-initialise and discard in-progress typing — the "can't type in the
  // address modal" freeze.
  // Read the address value out first so the memo only recomputes when the
  // address field itself changes - not on every keystroke of other fields
  // (depending on the whole hostValues object would defeat the memo). This
  // also stays eslint react-hooks/exhaustive-deps clean.
  const capturedAddressValue = compositeAddressAttr
    ? hostValues[compositeAddressAttr.code_name]
    : undefined
  const capturedAddress = useMemo(
    () =>
      compositeAddressAttr
        ? normaliseCapturedAddressObject(capturedAddressValue)
        : null,
    [compositeAddressAttr, capturedAddressValue],
  )
  const hasCapturedAddress = !!capturedAddress && formatCapturedAddress(capturedAddress).length > 0

  const openModal = () => {
    setSnapshot(hostValues)
    setModalOpen(true)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && snapshot !== null) {
      onValuesChange(snapshot)
      setSnapshot(null)
    }
    setModalOpen(open)
  }

  const handleSave = () => {
    setSnapshot(null)
    setModalOpen(false)
  }

  return (
    <div className="space-y-3">
      {compositeAddressAttr ? (
        <>
          <AddressCapture
            value={hasCapturedAddress ? capturedAddress : null}
            onChange={(nextAddress) => {
              const next = { ...hostValues }
              if (nextAddress) {
                const addressValue = resolvedToAddressFieldValue(nextAddress)
                next[compositeAddressAttr.code_name] = addressValue
                Object.assign(next, addressFieldValueToAttributeValues(addressValue, attributes))
              } else {
                next[compositeAddressAttr.code_name] = ''
              }
              onValuesChange(next)
            }}
            error={!hasCapturedAddress ? error : undefined}
            placeholder={addressPlaceholder}
            searchInputName={`${compositeAddressAttr.code_name}-search`}
          />
          {addressFieldAttrs.map((attr) =>
            anchoredExtras?.[attr.code_name] ? (
              <Fragment key={attr.code_name}>{anchoredExtras[attr.code_name]}</Fragment>
            ) : null,
          )}
        </>
      ) : (
        <>
          {/* Search bar + manual-entry hint (grouped so the hint sits tight
              to the input rather than inheriting the parent's space-y gap). */}
          <div>
            <div className="relative">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder={addressPlaceholder}
                className={error && !hasAddress ? 'border-destructive' : undefined}
              />
              {error && !hasAddress ? (
                <p className="mt-1 text-xs text-[var(--status-error)]">{error}</p>
              ) : null}
              {suggestions && suggestions.length > 0 && query.length > 2 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[240px] overflow-y-auto rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)]">
                  {suggestions.map((s) => (
                    <Button
                      key={s.id}
                      variant="ghost"
                      type="button"
                      onClick={() => handleSelect(s)}
                      className="h-auto w-full cursor-pointer justify-start rounded-none px-3 py-2 text-left text-[12px] font-light text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                    >
                      {s.address}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {!hasAddress && (
              <Button
                type="button"
                variant="ghost"
                onClick={openModal}
                className="mt-1.5 block h-auto px-0 py-0 text-[11px] font-light text-[var(--ink-3)] hover:text-[var(--green)]"
              >
                Enter address manually
              </Button>
            )}
          </div>

          {/* Saved address summary (lookup or manual - same pattern) */}
          {hasAddress && (
            <div className="space-y-1.5">
              <p className="m-0 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
                Address
              </p>
              <div className="flex items-center justify-between gap-3 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2">
                <p className="m-0 min-w-0 truncate text-[12px] font-light leading-[1.5] text-[var(--ink)]">
                  {formattedAddress || (
                    <span className="text-[var(--ink-3)]">No address details</span>
                  )}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={openModal}
                  aria-label="Edit address"
                  className="-mr-1 shrink-0 font-light text-[var(--ink-3)] hover:text-[var(--ink)]"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
              {/* Anchored extras for address fields render below the summary so
                  visibility-rule dependents (e.g. fields conditioned on a
                  postcode) still appear at the right point in the flow. */}
              {addressFieldAttrs.map((attr) =>
                anchoredExtras?.[attr.code_name] ? (
                  <Fragment key={attr.code_name}>
                    {anchoredExtras[attr.code_name]}
                  </Fragment>
                ) : null,
              )}
            </div>
          )}

          {/* Manual / edit address modal */}
          <Dialog open={modalOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{hasAddress ? 'Edit address' : 'Enter address'}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                {addressFieldAttrs.map((attr) => (
                  <MetadataField
                    key={attr.code_name}
                    field={normaliseTemplateAttribute(attr)}
                    value={hostValues[attr.code_name] ?? ''}
                    onChange={(v) => handleAddressValueChange(attr, v)}
                    labelVariant="compact"
                  />
                ))}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  className="font-light"
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleSave} className="font-light">
                  Save address
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Standard (non-lookup, non-prepopulated) fields */}
      {standardAttrs.length > 0 && (
        <div className="space-y-2">
          {standardAttrs.map((attr) => (
            <Fragment key={attr.code_name}>
              <MetadataField
                field={normaliseTemplateAttribute(attr)}
                value={hostValues[attr.code_name] ?? ''}
                onChange={(v) =>
                  onValuesChange({ ...hostValues, [attr.code_name]: v })
                }
                labelVariant="compact"
                error={attributeErrors?.[attr.code_name]}
              />
              {anchoredExtras?.[attr.code_name]}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Early attribute field renderer
// ---------------------------------------------------------------------------

function EarlyAttributeField({
  attr,
  value,
  onChange,
  error,
  required,
}: {
  attr: TemplateAttribute
  value: unknown
  onChange: (v: unknown) => void
  error?: string
  required?: boolean
}) {
  const field = useMemo(() => {
    const base = normaliseTemplateAttribute(attr)
    return required ? { ...base, required: true } : base
  }, [attr, required])
  return (
    <MetadataField
      field={field}
      value={value ?? ''}
      onChange={onChange}
      labelVariant="compact"
      error={error}
    />
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StepHostContext({
  state,
  update,
  next,
  back,
  hostTemplate: hostTemplateSummary,
  hostRequirement,
  earlyAttributes,
  geoEnabled,
  // Legacy gating prop - Continue is always enabled now and validation
  // happens inline via the wizard validation context. Kept in the type
  // for backward compatibility with the page's renderSegment helper but
  // intentionally not destructured here.
  continueHint,
  extraPanels,
  propertyExtras,
  anchoredFieldsByHostAttr,
  dataCaptureConfig,
  earlyCaptureFields = [],
  ruleContext,
  onStructurePickerActivation,
}: StepHostContextProps) {
  const { dcc, fieldLabel } = useDataCaptureConfig(dataCaptureConfig)
  const mailingVisible = earlyCaptureFields.includes('consumer_address') && isVisible(dcc?.consumer_address)
  const mailingRequired = mailingVisible && isRequired(dcc?.consumer_address)
  const mailingLabel = fieldLabel('consumer_address', 'Mailing address')
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  // When the structure picker is in control of the structure decision
  // (existing property with at least one structure), the host step must
  // hide and stop requiring the redundant standalone structure fields
  // (e.g. `structure_type`, `other_structure_type`). The picker reports
  // its control + the code_names it owns here; the type is derived from
  // the selected/created structure and already written to hostValues.
  const [structurePicker, setStructurePicker] =
    useState<StructurePickerActivation>({ active: false, ownedCodeNames: [] })
  const handleStructurePickerActivation = useCallback(
    (activation: StructurePickerActivation) => {
      setStructurePicker((prev) =>
        prev.active === activation.active &&
        prev.ownedCodeNames.join(',') === activation.ownedCodeNames.join(',')
          ? prev
          : activation,
      )
      // Bubble up so the page can drop picker-owned registration fields
      // from this step's required-field validation.
      onStructurePickerActivation?.(activation)
    },
    [onStructurePickerActivation],
  )
  const structurePickerOwnedCodes = useMemo(
    () =>
      structurePicker.active
        ? new Set(structurePicker.ownedCodeNames)
        : new Set<string>(),
    [structurePicker],
  )

  // A tenant has at most one partner-facing ExternalOrganisationRole
  // (e.g. "Contractor" for Supagard, "Installer" for OC). Use it to
  // both scope the picker's search and label the field dynamically, so
  // the UI always speaks the tenant's vocabulary.
  const { data: externalRoles = [] } = useExternalRoles()
  const { locale } = useFormatting()
  const partnerPickerScope = usePartnerPickerScope()
  const requirePartner = partnerPickerScope.required
  const contractorRole = externalRoles[0]
  const contractorRoleUuid = contractorRole?.uuid
  const contractorRoleName = contractorRole?.name ?? 'Installer / Contractor'
  const contractorRoleNameLower = contractorRoleName.toLowerCase()

  // Fetch full host template with attributes (summary from templates API doesn't include them)
  const { data: hostTemplate } = useHostTemplateDetail(hostTemplateSummary?.uuid ?? null)

  // Detail view for the selected partner - rendered inline beneath the
  // picker once a contractor is chosen, so the agent can sanity-check
  // membership ID, tier, etc. before proceeding. Cached by react-query
  // so reopening the step doesn't refetch.
  const { data: selectedPartnership } = usePartnershipDetail(
    state.partnershipUuid,
  )

  const hostValues = useMemo(
    () => state.hostAttributeValues ?? {},
    [state.hostAttributeValues],
  )
  const earlyValues = useMemo(
    () => state.earlyAttributes ?? {},
    [state.earlyAttributes],
  )

  const visibleHostAttributes = useMemo(() => {
    const attrs = hostTemplate?.attributes ?? []
    if (!ruleContext) return attrs
    return attrs.filter((attr) => {
      const registrationConfig = attr.configuration_data?.registration
      const conditions =
        registrationConfig &&
        typeof registrationConfig === 'object' &&
        'visibility_conditions' in registrationConfig
          ? ((registrationConfig as { visibility_conditions?: PredicateNode | null })
              .visibility_conditions ?? null)
          : null
      if (!conditions) return true
      const result = evaluatePredicateLocally(conditions, ruleContext)
      return result.supported && result.matched
    })
  }, [hostTemplate?.attributes, ruleContext])

  // Host attributes actually rendered in the Property panel. When the
  // structure picker is in control, its owned fields (structure_type,
  // other_structure_type, ...) are hidden here because the picker is the
  // single structure decision; their values still flow via hostValues.
  const renderedHostAttributes = useMemo(() => {
    if (structurePickerOwnedCodes.size === 0) return visibleHostAttributes
    return visibleHostAttributes.filter(
      (a) => !structurePickerOwnedCodes.has(a.code_name),
    )
  }, [visibleHostAttributes, structurePickerOwnedCodes])

  // Anchored registration-field nodes, minus any anchored to a host
  // attribute the structure picker owns (e.g. `other_structure_type`
  // revealed by `structure_type`). Those belong to the picker decision.
  const renderedAnchoredFields = useMemo(() => {
    if (!anchoredFieldsByHostAttr || structurePickerOwnedCodes.size === 0) {
      return anchoredFieldsByHostAttr
    }
    const out: Record<string, React.ReactNode> = {}
    for (const [code, node] of Object.entries(anchoredFieldsByHostAttr)) {
      if (!structurePickerOwnedCodes.has(code)) out[code] = node
    }
    return out
  }, [anchoredFieldsByHostAttr, structurePickerOwnedCodes])

  // Resolve postal code from host values for geo lookup
  const postalCode = useMemo(() => {
    const postcodeAttr = visibleHostAttributes.find(
      (a) => a.maps_to_column === 'address_postcode',
    )
    if (postcodeAttr) {
      const value = String(hostValues[postcodeAttr.code_name] ?? '')
      if (value) return value
    }
    const addressAttr = visibleHostAttributes.find((a) => a.field_type === 'ADDRESS')
    return addressAttr ? addressPostalCode(hostValues[addressAttr.code_name]) : ''
  }, [visibleHostAttributes, hostValues])

  // Geo eligibility lookup needs an installation-date-shaped early
  // attribute. The early-attribute mechanism is generic; the canonical
  // `installation_date` code_name is provided per-tenant via
  // ProductTemplate.early_registration_attributes (see seed_oc /
  // seed_property_tenant). Tenants that don't configure it simply skip
  // the geo step.
  const installationDate = String(earlyValues.installation_date ?? '')

  // Per-field required validation. Driven by:
  //   - `hostRequirement === 'REQUIRED'` → postcode must be filled.
  //   - `geoEnabled` → both postcode + installation date are needed
  //     for the eligibility lookup.
  // The address postcode is captured inside the AddressPanel modal so
  // we can't paint a single input red; instead we surface the error as
  // a hint above the panel when attempted. Installation date *is* an
  // inline early attribute, so it gets the standard red-border + hint
  // treatment via MetadataField.
  const installationDateAttr = useMemo(
    () => earlyAttributes.find((a) => a.code_name === 'installation_date'),
    [earlyAttributes],
  )
  // A postcode can only be *required* when there is actually a property
  // panel on screen to capture it. Templates with no host_template (e.g. a
  // fresh / catalog-only product type) render no address field, so requiring
  // a postcode there would register a validator that can never be satisfied
  // and silently dead-ends Continue with no visible error.
  // Use the synchronous `hostTemplateSummary` prop (not the async
  // `useHostTemplateDetail` result) so the requirement is correct on first
  // render - otherwise Continue could bypass postcode validation during the
  // brief window before the detail query resolves.
  const postcodeRequired =
    !!hostTemplateSummary && (hostRequirement === 'REQUIRED' || geoEnabled)
  const installationDateRequired = !!(geoEnabled && installationDateAttr)
  // The contractor picker only renders for the `installer_name` early
  // attribute. If the template doesn't surface it there is no input to
  // satisfy a partner requirement, so don't block on it.
  const contractorFieldRendered = earlyAttributes.some(
    (a) => a.code_name === 'installer_name',
  )

  // Other required host attributes (e.g. `structure_type`) - anything
  // marked `required` on the template that isn't an address-lookup or
  // prepopulated address field. The address-related attributes are
  // covered by the `address_postcode` spec above; standalone attributes
  // need their own per-field validation so the user can't bypass them.
  const requiredStandardHostAttrs = useMemo(() => {
    return visibleHostAttributes.filter(
      (a) =>
        a.required &&
        !a.is_prepopulated &&
        a.lookup_provider !== 'address' &&
        // Skip fields owned by the structure picker: it is the single
        // structure decision and writes their values into hostValues, so
        // requiring the standalone dropdowns would be redundant and could
        // throw a false "required" error after a structure is selected.
        !structurePickerOwnedCodes.has(a.code_name),
    )
  }, [visibleHostAttributes, structurePickerOwnedCodes])

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    const specs: RequiredFieldSpec[] = []
    if (postcodeRequired) {
      specs.push({
        key: 'address_postcode',
        label: 'Address',
        isFilled: postalCode.trim().length > 0,
        message: 'Address with a postcode is required',
      })
    }
    if (installationDateRequired) {
      specs.push({
        key: 'installation_date',
        label: installationDateAttr?.display_name ?? 'Installation date',
        isFilled: installationDate.length > 0,
      })
    }
    if (requirePartner && contractorFieldRendered) {
      specs.push({
        key: 'contractor',
        label: contractorRoleName,
        isFilled: !!state.partnershipUuid,
        message: `${contractorRoleName} is required`,
      })
    }
    for (const attr of requiredStandardHostAttrs) {
      specs.push({
        key: `host:${attr.code_name}`,
        label: attr.display_name,
        isFilled: isValueFilled(hostValues[attr.code_name]),
      })
    }
    return specs
  }, [
    postcodeRequired,
    installationDateRequired,
    installationDateAttr,
    postalCode,
    installationDate,
    requirePartner,
    contractorFieldRendered,
    contractorRoleName,
    state.partnershipUuid,
    requiredStandardHostAttrs,
    hostValues,
  ])
  const errors = useRequiredFieldErrors('host_context', requiredSpecs)
  const addressError = errors['address_postcode']
  const hostAttributeErrors = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    for (const attr of requiredStandardHostAttrs) {
      const msg = errors[`host:${attr.code_name}`]
      if (msg) out[attr.code_name] = msg
    }
    return out
  }, [errors, requiredStandardHostAttrs])

  async function handleNext() {
    if (loading) return
    if (geoEnabled && postalCode && installationDate) {
      setLoading(true)
      try {
        // Strip empty host attribute values so the backend skips
        // narrowing entirely when the user hasn't filled anything in.
        const cleanedHostAttrs: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(hostValues)) {
          if (v !== undefined && v !== null && v !== '') cleanedHostAttrs[k] = v
        }
        const resp = await api.post('/geo/eligibility/', {
          postal_code: postalCode,
          reference_date: installationDate,
          // Category-aware PSA filtering is opt-in per template: forward the
          // step-1 product template so the backend can decide whether this
          // category is geo-restricted at all, and derive the business line
          // from the template's marker (no hardcoded 'Shingle' here).
          ...(state.productTemplateUuid
            ? { product_template_uuid: state.productTemplateUuid }
            : {}),
          ...(Object.keys(cleanedHostAttrs).length > 0
            ? { host_attrs: cleanedHostAttrs }
            : {}),
        })
        const data = resp.data
        const eligibility: GeoEligibilityResult = {
          zone: data.zone,
          psa: data.psa,
          method: data.resolution_method,
          confidence: data.resolution_confidence,
          productUuids: (data.products ?? []).map((p: { uuid: string }) => p.uuid),
          products: data.products ?? [],
          productCount: data.product_count ?? 0,
          psaFilteringApplied: data.psa_filtering_applied ?? true,
        }
        update({ geoEligibility: eligibility })
      } catch {
        // If geo fails, proceed without filtering
        update({ geoEligibility: null })
      } finally {
        setLoading(false)
      }
    }
    next()
  }

  return (
    <div className="w-full">
      <RegistrationStepGrid className="items-start">
        {/* Left column - Property + Mailing address stacked so the mailing
            panel stays pinned directly under the property panel regardless
            of how tall the right-hand Registration Details column grows
            (e.g. when the installer picker expands to show contractor
            details). */}
        {hostTemplate && (
          <div className="flex flex-col gap-6">
            <RegistrationPanel
              title={hostTemplate.name}
              icon={<Home className="h-3.5 w-3.5 text-[var(--ink-3)]" />}
            >
              <AddressPanel
                attributes={[...renderedHostAttributes].sort((a, b) => a.order - b.order)}
                hostValues={hostValues}
                onValuesChange={(values) => update({ hostAttributeValues: values })}
                addressPlaceholder={t('wizard.addressPlaceholder', {
                  defaultValue: 'Start typing an address or postcode',
                })}
                anchoredExtras={renderedAnchoredFields}
                error={addressError}
                attributeErrors={hostAttributeErrors}
              />
              {/*
                Tenant-defined RegistrationFields whose capture step is
                `host_context` but which are *not* anchored to a specific
                host attribute by a visibility rule. Anchored dependents
                (e.g. `other_structure_type` revealed when
                `structure_type === "Other"`) render inline directly under
                their trigger via `AddressPanel`'s `anchoredExtras` slot.
              */}
              {propertyExtras}
            </RegistrationPanel>

            {/* Structure picker - only renders when the chosen property
                already exists and the tenant has a structure entity.
                Lets the operator attach this registration to one of the
                property's existing structures, or add a new one inline. */}
            <StructurePickerPanel
              state={state}
              update={update}
              onActivationChange={handleStructurePickerActivation}
            />

            {/* Mailing address - rendered inside the left column so the
                user can toggle "Same as installation" while the
                installation address is still in view. Hidden for tenants
                that don't collect a mailing address. */}
            {mailingVisible && (
              <MailingAddressPanel
                state={state}
                update={update}
                label={mailingLabel}
                required={mailingRequired}
                hostAttributes={hostTemplate.attributes}
              />
            )}
          </div>
        )}

        {/* Right column - Early attributes (installation date, installer, etc.)
            The ``installer_name`` attribute is rendered as a contractor
            picker instead of a free-text input: picking a contractor
            sets the partnership on the wizard state (so tier-gated
            warranty templates narrow in step 4) and mirrors the
            contractor's display name into the EAV value so existing
            readers still see an installer name. */}
        {earlyAttributes.length > 0 && (
          <RegistrationPanel
            title="Registration Details"
            icon={<MapPin className="h-3.5 w-3.5 text-[var(--ink-3)]" />}
            bodyClassName="gap-3"
          >
              {earlyAttributes.map((attr) => {
                // installer_name is rendered as a contractor picker so the
                // Pro Agent can attach the registration to the partnership
                // (and tier) that did the install. See the panel comment
                // above for the full rationale.
                if (attr.code_name === 'installer_name') {
                  return (
                    <Fragment key={attr.code_name}>
                      <InlineDealerField
                        state={state}
                        update={update}
                        label={contractorRoleName}
                        searchPlaceholder={`Search ${contractorRoleNameLower}s...`}
                        emptyMessage={`No ${contractorRoleNameLower}s found`}
                        error={errors['contractor']}
                        searchOptions={{
                          includeLocations: true,
                          role: contractorRoleUuid,
                        }}
                        secondaryAttributeCodes={[
                          'location_name',
                          'city',
                          'state',
                          'membership_number',
                        ]}
                        badgeAttributeCode="tier"
                        // Not every contractor is in the partner network, so
                        // the agent can capture one inline. This creates a
                        // real partnership - searchable afterwards, with its
                        // address and contacts - rather than the free-text
                        // name the earlier ``allowCreate`` row stored
                        // (WPS-163).
                        allowRecordCreate
                        createRoleUuid={contractorRoleUuid}
                        createRoleName={contractorRoleName}
                        onCreatedPartner={(partnership) =>
                          update({
                            earlyAttributes: {
                              ...earlyValues,
                              installer_name:
                                partnership.downstream_organisation.name,
                            },
                          })
                        }
                        onSelectExistingPartner={(candidate) =>
                          update({
                            earlyAttributes: {
                              ...earlyValues,
                              installer_name: candidate.name,
                            },
                          })
                        }
                        createdName={
                          !state.partnershipUuid
                            ? String(earlyValues.installer_name ?? '') || null
                            : null
                        }
                        onSelect={(p) =>
                          update({
                            earlyAttributes: {
                              ...earlyValues,
                              installer_name: p.downstream_organisation.name,
                            },
                          })
                        }
                        onCreate={(name) =>
                          update({
                            earlyAttributes: {
                              ...earlyValues,
                              installer_name: name,
                            },
                          })
                        }
                        onClear={() =>
                          update({
                            earlyAttributes: {
                              ...earlyValues,
                              installer_name: '',
                            },
                          })
                        }
                        onClearCreated={() =>
                          update({
                            earlyAttributes: {
                              ...earlyValues,
                              installer_name: '',
                            },
                          })
                        }
                      />
                      {state.partnershipUuid && selectedPartnership && (
                        <PartnerProfilePanel
                          partnership={selectedPartnership}
                          variant="inset"
                        />
                      )}
                    </Fragment>
                  )
                }
                return (
                  <EarlyAttributeField
                    key={attr.code_name}
                    attr={attr}
                    value={earlyValues[attr.code_name]}
                    onChange={(v) =>
                      update({
                        earlyAttributes: { ...earlyValues, [attr.code_name]: v },
                      })
                    }
                    error={errors[attr.code_name]}
                    required={
                      attr.code_name === 'installation_date'
                        ? installationDateRequired
                        : undefined
                    }
                  />
                )
              })}
          </RegistrationPanel>
        )}

        {extraPanels}
      </RegistrationStepGrid>

      {/* Geo resolution hint */}
      {geoEnabled && postalCode && installationDate && (
        <p className="mt-4 text-[11px] text-[var(--ink-3)]">
          <MapPin className="mr-1 inline h-3 w-3" />
          Products will be filtered by availability for {postalCode} on{' '}
          {new Date(installationDate + 'T00:00:00').toLocaleDateString(locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>
      )}

      {/* Continue */}
      <RegistrationStepActions
        onBack={back}
        onContinue={handleNext}
        continueLabel={loading ? 'Checking availability...' : 'Continue'}
        continueHint={loading ? null : continueHint}
        align="end"
      />
    </div>
  )
}
